import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const PHONE = "970-402-3530";
const EMPTY = { name: "", phone: "", email: "", property_type: "residential", size: "", notes: "" };

export const Estimate = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = "h-12 w-full rounded-none border-white/15 bg-[#111317] text-base text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-[var(--acid)]";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/estimates", form);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative border-y border-white/10 bg-[#0D0E11]" data-testid="estimate-section">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-24 sm:px-8 sm:py-28 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-6">( Free estimate )</p>
          <h2 className="font-display text-5xl uppercase leading-[0.9] text-zinc-50 sm:text-7xl">
            What would <span className="font-serif-i normal-case text-acid">yours</span> cost?
          </h2>
          <p className="mt-6 max-w-md text-lg text-zinc-300">Tell us about your space and we&apos;ll come see it in person — the walkthrough is free, and the quote is tailored to what your place actually needs.</p>
          <p className="mt-4 max-w-md text-sm text-zinc-400">No two cleans are alike, so no two prices are either. No payment, no pressure, no &ldquo;starting at $99&rdquo; games.</p>
          <a href={`sms:+19704023530`} data-testid="estimate-text-us-button"
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-zinc-100 transition-colors duration-300 hover:border-[var(--acid)] hover:text-[var(--acid)]">
            <MessageSquareText className="h-4 w-4" /> Rather text? Text us — {PHONE}
          </a>
        </div>
        <div className="lg:col-span-7">
          {done ? (
            <div className="flex h-full min-h-[320px] flex-col items-start justify-center border border-white/10 bg-[#111317] p-9" data-testid="estimate-success">
              <CheckCircle2 className="h-10 w-10 text-[var(--acid)]" />
              <p className="mt-5 font-display text-3xl uppercase text-zinc-50">Request sent.</p>
              <p className="mt-3 max-w-md text-zinc-400">It&apos;s in our inbox with all your details. We&apos;ll reach out shortly to set up your free walkthrough. Impatient? Just text us.</p>
            </div>
          ) : (
            <motion.form onSubmit={submit} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="border border-white/10 bg-[#111317] p-7 sm:p-9" data-testid="estimate-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <input value={form.name} onChange={set("name")} placeholder="Your name" required maxLength={80} className={field} data-testid="estimate-input-name" />
                <input value={form.phone} onChange={set("phone")} placeholder="Phone" required maxLength={40} className={field} data-testid="estimate-input-phone" />
                <input value={form.email} onChange={set("email")} type="email" placeholder="Email" required className={field} data-testid="estimate-input-email" />
                <select value={form.property_type} onChange={set("property_type")} className={`${field} appearance-none`} data-testid="estimate-select-type">
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
                <input value={form.size} onChange={set("size")} placeholder="Size — beds, baths, sq ft or crew size" maxLength={120} className={`${field} sm:col-span-2`} data-testid="estimate-input-size" />
                <textarea value={form.notes} onChange={set("notes")} rows={4} maxLength={1000}
                  placeholder="What needs saving? Pets, floorings, that one closet…" className="w-full border border-white/15 bg-[#111317] p-4 text-base text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-[var(--acid)] sm:col-span-2" data-testid="estimate-input-notes" />
              </div>
              <button type="submit" disabled={busy} data-testid="estimate-submit-button"
                className="mt-6 w-full rounded-full bg-acid py-4 font-bold text-black transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50 sm:w-auto sm:px-10">
                Get my free estimate
              </button>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
};
